import * as React from "react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import {
  Table,
  TableBody,
  TableCell,
  TableCellLayout,
  TableColumnDefinition,
  TableColumnSizingOptions,
  TableHeader,
  TableHeaderCell,
  TableRow,
  createTableColumn,
  useTableColumnSizing_unstable,
  useTableFeatures,
  Checkbox,
  Button,
  makeStyles,
} from "@fluentui/react-components";
import { EditRegular, DeleteRegular } from "@fluentui/react-icons";
import vertragsdaten from "../data/viewVerträgeÜbersicht.json";
import CommandBar from "./CommandBar";
import { apiUrl } from "../utils/api";

const useStyles = makeStyles({
  stripedRow: {
    backgroundColor: "#f2f2f2",
  },
  cellEllipsis: {
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
});

type Vertrag = (typeof vertragsdaten)[number];

type BackendContract = {
  id: number;
  contractNbr: string;
  insuranceNbr: string | null;
  branch: string | null;
  customerNumber: string | null;
  paymentMethodID: number | null;
  riskLocation: string | null;
  remark: string | null;
  mainDueDate: string | null;
  paymentFrequency: string | null;
  annualFee: number | null;
};

type ContractTableContactProps = {
  kontaktId: number;
};

const ContractTableContact: React.FC<ContractTableContactProps> = ({
  kontaktId,
}) => {
  const classes = useStyles();
  const navigate = useNavigate();
  const [vertrag, setVertrag] = useState<Vertrag[]>([]);
  const [search, setSearch] = useState("");
  const [selectedIds, setSelectedIds] = React.useState<Set<number>>(new Set());

  // Verträge für einen Kontakt vom Backend laden
  useEffect(() => {
    const fetchContracts = async () => {
      try {
        const res = await fetch(
          apiUrl(`contracts/by-contact/${kontaktId}`, { limit: 50, offset: 0 }),
        );
        if (!res.ok) {
          console.error(
            "Failed to load contracts for contact",
            kontaktId,
            res.status,
            res.statusText,
          );
          return;
        }
        const data = (await res.json()) as BackendContract[];
        const mapped: Vertrag[] = data.map((c) =>
          ({
            id: c.id,
            Vertragsnummer: c.contractNbr,
            Versicherungsnummer: c.insuranceNbr ?? "",
            Zweig: c.branch ?? "",
            Kundennummer: c.customerNumber ?? "",
            Risikoort: c.riskLocation ?? "",
            HauptFaelligkeit: c.mainDueDate ?? "",
            Jahresbeitrag: c.annualFee ?? 0,
            Zahlweise: c.paymentFrequency ?? "",
            Berater: "",
            KundeName: "",
          } as unknown as Vertrag),
        );
        setVertrag(mapped);
      } catch (error) {
        console.error("Error fetching contracts for contact", kontaktId, error);
      }
    };

    if (kontaktId) {
      fetchContracts();
    }
  }, [kontaktId]);

  // Spalten-Definition mit createTableColumn
  const columns: TableColumnDefinition<Vertrag>[] = React.useMemo(
    () => [
      createTableColumn<Vertrag>({
        columnId: "select",
        renderHeaderCell: () => <></>,
      }),
      createTableColumn<Vertrag>({
        columnId: "Vertragsnummer",
        renderHeaderCell: () => <>Vertragsnummer</>,
        renderCell: (item) => <>{item.Vertragsnummer}</>,
      }),
      createTableColumn<Vertrag>({
        columnId: "Versicherungsnummer",
        renderHeaderCell: () => <>Versicherungsnummer</>,
        renderCell: (item) => <>{item.Versicherungsnummer}</>,
      }),
      createTableColumn<Vertrag>({
        columnId: "Zweig",
        renderHeaderCell: () => <>Zweig</>,
        renderCell: (item) => <>{item.Zweig}</>,
      }),
      createTableColumn<Vertrag>({
        columnId: "Kundennummer",
        renderHeaderCell: () => <>Kundennummer</>,
        renderCell: (item) => <>{item.Kundennummer}</>,
      }),
      createTableColumn<Vertrag>({
        columnId: "Risikoort",
        renderHeaderCell: () => <>Risikoort</>,
        renderCell: (item) => <>{item.Risikoort}</>,
      }),
      createTableColumn<Vertrag>({
        columnId: "Hauptfälligkeit",
        renderHeaderCell: () => <>Hauptfälligkeit</>,
        renderCell: (item) => <>{item.HauptFaelligkeit}</>,
      }),
      createTableColumn<Vertrag>({
        columnId: "Jahresbeitrag",
        renderHeaderCell: () => <>Jahresbeitrag</>,
        renderCell: (item) => <>{item.Jahresbeitrag}</>,
      }),
      createTableColumn<Vertrag>({
        columnId: "Zahlweise",
        renderHeaderCell: () => <>Zahlweise</>,
        renderCell: (item) => <>{item.Zahlweise}</>,
      }),
      createTableColumn<Vertrag>({
        columnId: "Berater",
        renderHeaderCell: () => <>Berater</>,
        renderCell: (item) => <>{item.Berater}</>,
      }),
      createTableColumn<Vertrag>({
        columnId: "Kunde",
        renderHeaderCell: () => <>Kunde</>,
        renderCell: (item) => <>{item.KundeName}</>,
      }),
    ],
    [],
  );

  // ColumnSizingOptions definieren
  const columnSizingOptions: TableColumnSizingOptions = React.useMemo(
    () => ({
      select: { defaultWidth: 48, minWidth: 48 },
      RisikoOrt: { defaultWidth: 340, minWidth: 150 },
      Zweig: { defaultWidth: 120, minWidth: 80 },
      Vertragsnummer: { defaultWidth: 195, minWidth: 100 },
      KontaktName: { defaultWidth: 255, minWidth: 120 },
      actions: { defaultWidth: 100, minWidth: 80 },
    }),
    [],
  );

  const filteredContracts = vertrag.filter((k) =>
    (k.Risikoort ?? "").toLowerCase().includes(search.toLowerCase()),
  );
  // Handlers for CommandBar
  const handleSearchChange = (value: string) => {
    setSearch(value);
  };
  const handleNew = () => {
    navigate("/vertrag/new", { state: { kontaktId } });
  };

  // Table-Features inklusive ColumnSizing
  const { getRows, tableRef, columnSizing_unstable } = useTableFeatures(
    { columns, items: filteredContracts },
    [useTableColumnSizing_unstable({ columnSizingOptions })],
  );

  // Auswahl-Handler
  const toggleSelect = (id: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  // Navigation zur Vertragsdetail-Ansicht
  const handleEdit = (item: Vertrag) => {
    navigate(`/contracts/${item.id}`, {
      state: {
        contract: item,
        kontaktId,
      },
    });
  };

  // Löschen
  const handleDelete = (id: number) => {
    setVertrag((prev) => prev.filter((k) => k.id !== id));
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  };

  const rows = getRows();

  return (
    <div style={{ fontSize: "14px", width: "100vw", overflowX: "auto" }}>
      <CommandBar
        header="Verträge"
        onSearchChange={handleSearchChange}
        onNewClick={handleNew}
      />
      <Table
        ref={tableRef}
        aria-label="Risiken-Tabelle"
        {...columnSizing_unstable.getTableProps()}
      >
        <TableHeader>
          <TableRow>
            {columns.map((col) => (
              <TableHeaderCell
                key={col.columnId}
                {...columnSizing_unstable.getTableHeaderCellProps(col.columnId)}
              >
                {col.columnId === "select" ? null : col.renderHeaderCell()}
              </TableHeaderCell>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map(({ item }, i) => (
            <TableRow
              key={item.id}
              className={i % 2 === 1 ? classes.stripedRow : undefined}
            >
              <TableCell
                {...columnSizing_unstable.getTableCellProps("select")}
                onClick={(e) => e.stopPropagation()}
              >
                <Checkbox
                  checked={selectedIds.has(item.id)}
                  onClick={(e) => e.stopPropagation()}
                  onChange={() => toggleSelect(item.id)}
                />
              </TableCell>
              <TableCell
                {...columnSizing_unstable.getTableCellProps("Vertragsnummer")}
                onClick={() => handleEdit(item)}
              >
                <div
                  className={classes.cellEllipsis}
                  title={item.Vertragsnummer}
                >
                  {item.Vertragsnummer}
                </div>
              </TableCell>
              <TableCell
                {...columnSizing_unstable.getTableCellProps(
                  "Versicherungsnummer",
                )}
                onClick={() => handleEdit(item)}
              >
                <div
                  className={classes.cellEllipsis}
                  title={item.Versicherungsnummer}
                >
                  {item.Versicherungsnummer}
                </div>
              </TableCell>
              <TableCell
                {...columnSizing_unstable.getTableCellProps("Zweig")}
                onClick={() => handleEdit(item)}
              >
                <div className={classes.cellEllipsis} title={item.Zweig}>
                  {item.Zweig}
                </div>
              </TableCell>
              <TableCell
                {...columnSizing_unstable.getTableCellProps("Kundennummer")}
                onClick={() => handleEdit(item)}
              >
                <div className={classes.cellEllipsis} title={item.Kundennummer}>
                  {item.Kundennummer}
                </div>
              </TableCell>
              <TableCell
                {...columnSizing_unstable.getTableCellProps("Risikoort")}
                onClick={() => handleEdit(item)}
              >
                <div className={classes.cellEllipsis} title={item.Risikoort}>
                  {item.Risikoort}
                </div>
              </TableCell>
              <TableCell
                {...columnSizing_unstable.getTableCellProps("Hauptfälligkeit")}
                onClick={() => handleEdit(item)}
              >
                <div
                  className={classes.cellEllipsis}
                  title={item.HauptFaelligkeit}
                >
                  {item.HauptFaelligkeit}
                </div>
              </TableCell>
              <TableCell
                {...columnSizing_unstable.getTableCellProps("Jahresbeitrag")}
                onClick={() => handleEdit(item)}
              >
                <div
                  className={classes.cellEllipsis}
                  title={"" + item.Jahresbeitrag}
                >
                  {item.Jahresbeitrag}
                </div>
              </TableCell>
              <TableCell
                {...columnSizing_unstable.getTableCellProps("Zahlweise")}
                onClick={() => handleEdit(item)}
              >
                <div className={classes.cellEllipsis} title={item.Zahlweise}>
                  {item.Zahlweise}
                </div>
              </TableCell>
              <TableCell
                {...columnSizing_unstable.getTableCellProps("Berater")}
                onClick={() => handleEdit(item)}
              >
                <div className={classes.cellEllipsis} title={item.Berater}>
                  {item.Berater}
                </div>
              </TableCell>
              <TableCell
                {...columnSizing_unstable.getTableCellProps("Kunde")}
                onClick={() => handleEdit(item)}
              >
                <div className={classes.cellEllipsis} title={item.KundeName}>
                  {item.KundeName}
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};

export default ContractTableContact;
